/**
 * Adapter `linkComponent` para Polaris AppProvider.
 *
 * Polaris renderiza anchors nativos por defecto cuando un componente recibe
 * `url` (Navigation, Button, Banner.action, Link, Breadcrumb…). Sin adapter,
 * cada click provoca una recarga completa de página.
 *
 * Este componente reemplaza esos anchors por React Router para que la
 * navegación sea SPA — manteniendo las ventajas de los enlaces reales:
 * right-click → copiar URL, middle-click → nueva pestaña, hover → URL en
 * status bar, historial del navegador, shareable deep-links.
 *
 * Enlaces externos (http://, https://, mailto:, tel:, target=_blank) se
 * renderizan como <a> normal — el adapter solo intercepta rutas internas.
 */

import { forwardRef, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Extiende los atributos nativos de <a> (cubre aria-*, data-*, rel, etc. que
 * Polaris reenvía al linkComponent) y sobrescribe sólo lo que el adapter trata
 * de forma especial. Se evita un index signature `[key: string]: unknown`
 * porque ensancharía las props nombradas (url, target…) al tipo del índice.
 */
interface PolarisLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  url: string;
  external?: boolean;
  onClick?: (event?: MouseEvent<HTMLAnchorElement>) => void;
  children?: ReactNode;
}

function isExternalUrl(url: string, external?: boolean, target?: string): boolean {
  if (external) return true;
  if (target === '_blank') return true;
  return /^(https?:|mailto:|tel:|ftp:|#)/i.test(url);
}

export const PolarisLink = forwardRef<HTMLAnchorElement, PolarisLinkProps>(
  function PolarisLink({ url, external, target, onClick, children, className, ...rest }, ref) {
    if (isExternalUrl(url, external, target)) {
      return (
        <a
          ref={ref}
          href={url}
          target={target ?? (external ? '_blank' : undefined)}
          rel={external || target === '_blank' ? 'noopener noreferrer' : undefined}
          onClick={onClick}
          className={className}
          {...rest}
        >
          {children}
        </a>
      );
    }
    return (
      <Link
        ref={ref}
        to={url}
        onClick={onClick as unknown as (e: MouseEvent<HTMLAnchorElement>) => void}
        className={className}
        {...rest}
      >
        {children}
      </Link>
    );
  },
);
